import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useUserMetrics } from "@/hooks/useUserMetrics";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Upload, 
  Trophy, 
  MapPin, 
  Activity, 
  TrendingUp,
  Calendar,
  Mountain,
  Route
} from "lucide-react";

const Home = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { metrics, isLoading: metricsLoading } = useUserMetrics();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/auth');
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome back to the trails! 🏔️
          </h1>
          <p className="text-muted-foreground">
            Ready for your next adventure? Here's what's happening in your off-road world.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upload CTA Card */}
            <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Log Your Latest Run</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Share your adventure and help the community discover new trails
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate('/log-run')}>
                  <Upload className="h-4 w-4 mr-2" />
                  Log a Run
                </Button>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Mountain className="h-4 w-4 text-primary" />
                    <div>
                      <div className="text-2xl font-bold">
                        {metricsLoading ? '...' : metrics.trailsCompleted}
                      </div>
                      <div className="text-xs text-muted-foreground">Trails Completed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Route className="h-4 w-4 text-green-500" />
                    <div>
                      <div className="text-2xl font-bold">
                        {metricsLoading ? '...' : metrics.milesCompleted}
                      </div>
                      <div className="text-xs text-muted-foreground">Miles Driven</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold">0</div>
                      <div className="text-xs text-muted-foreground">Badges Earned</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-500" />
                    <div>
                      <div className="text-2xl font-bold">
                        {metricsLoading ? '...' : metrics.runsThisMonth}
                      </div>
                      <div className="text-xs text-muted-foreground">This Month</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity Feed */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {metricsLoading ? (
                  <div className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-muted-foreground">Loading activity...</p>
                  </div>
                ) : metrics.recentRuns.length > 0 ? (
                  metrics.recentRuns.map((run) => {
                    const trailName = run.run_segments?.[0]?.trail_segment?.trail_name || 'Unknown Trail';
                    const difficulty = run.run_segments?.[0]?.trail_segment?.difficulty;
                    const daysAgo = Math.floor((new Date().getTime() - new Date(run.created_at).getTime()) / (1000 * 60 * 60 * 24));
                    const timeAgo = daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
                    
                    return (
                      <div 
                        key={run.id} 
                        className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => navigate(`/runs/${run.id}`)}
                      >
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Mountain className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">You completed {trailName}</p>
                          <p className="text-xs text-muted-foreground">
                            {timeAgo} • {run.distance_miles?.toFixed(1) || '0'} miles
                          </p>
                        </div>
                        {difficulty && (
                          <Badge variant={
                            difficulty === 'easy' ? 'secondary' :
                            difficulty === 'moderate' ? 'default' : 'destructive'
                          }>
                            {difficulty}
                          </Badge>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Mountain className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No recent runs</p>
                    <p className="text-xs text-muted-foreground mt-1">Upload your first GPX track to get started!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Next Badge Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Next Badge
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Desert Explorer</span>
                    <span className="text-xs text-muted-foreground">7/10</span>
                  </div>
                  <Progress value={70} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    Complete 3 more desert trails to earn this badge
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Trending Trails */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Trending This Week
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Pismo Beach Dunes</p>
                    <p className="text-xs text-muted-foreground">23 new runs</p>
                  </div>
                  <Badge variant="secondary">Easy</Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Corral Canyon</p>
                    <p className="text-xs text-muted-foreground">18 new runs</p>
                  </div>
                  <Badge variant="secondary">Moderate</Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Rattlesnake Canyon</p>
                    <p className="text-xs text-muted-foreground">12 new runs</p>
                  </div>
                  <Badge variant="outline">Hard</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Community Events
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">Big Bear Group Run</p>
                  <p className="text-xs text-muted-foreground">Saturday, Dec 16 • 8:00 AM</p>
                  <Button size="sm" variant="outline" className="mt-2 w-full">
                    Join Event
                  </Button>
                </div>
                
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">Desert Cleanup Day</p>
                  <p className="text-xs text-muted-foreground">Sunday, Dec 24 • 9:00 AM</p>
                  <Button size="sm" variant="outline" className="mt-2 w-full">
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/explore')}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Explore Trails
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/my-trails')}>
                  <Route className="h-4 w-4 mr-2" />
                  My Trails
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/community')}>
                  <Activity className="h-4 w-4 mr-2" />
                  Community
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;